import { useMemo } from 'react';

import { Dropdown } from 'components/UI/Dropdown';
import { Icon } from 'components/UI/Icons';
import { Svgs } from 'environment';
import {
	useExpandGroupsContext,
	usePermissions,
	useProject,
	useProjectId,
	useVisibleVariableReferences
} from 'hooks/store';

import { DownloadFormat } from 'api/data/entries';
import { ExportType } from 'consts';
import { useLanguage } from 'features/hooks/useLanguage';
import { isGroupData } from 'helpers/variables';
import { useModalState } from 'hooks/ui';
import { VariableSetData } from 'store/data/variables';
import { LanguageType } from 'types';
import { Container } from './SeriesOptions.style';

export interface ExportOptions {
	type: ExportType;
	format: DownloadFormat;
	showFilterSection?: boolean;
}

interface CurrentSeriesProps {
	label: string;
	value: string;
	checked: boolean;
}
interface Props {
	currentSeriesItems: CurrentSeriesProps[];
	seriesData: VariableSetData;
}

export function SeriesOptions({ currentSeriesItems, seriesData }: Props) {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const [projectId] = useProjectId();

	const [{ data: project }] = useProject();

	const { expandedSeriesGroups, collapseAllSeriesGroups, expandAllSeriesGroups } =
		useExpandGroupsContext();

	const selectedColumnsForExport = useVisibleVariableReferences(currentSeriesItems.length === 1);

	const allowToggleGroupsFeature = Object.keys(expandedSeriesGroups).length > 0;
	const { hasExportDataAccess } = usePermissions();

	const canExportDataSet = project?.projectActions?.canExportDataSet ?? false;
	const isExportDenied = !hasExportDataAccess || !canExportDataSet;
	const exportDisabledTooltip = isExportDenied ? translation.exportDisabledTooltip : undefined;

	const canCollapseGroups = useMemo(() => {
		let canCollapse = false;

		Object.keys(expandedSeriesGroups).forEach(expanded => {
			seriesData.setData.forEach(groupData => {
				if (isGroupData(groupData)) {
					if (expanded === groupData.groupName && expandedSeriesGroups[expanded]) {
						canCollapse = true;
					}
				}
			});
		});

		return canCollapse;
	}, [expandedSeriesGroups]);

	const canExpandGroups = useMemo(() => {
		let canExpand = false;
		Object.keys(expandedSeriesGroups).forEach(expanded => {
			seriesData.setData.forEach(groupData => {
				if (isGroupData(groupData)) {
					if (expanded === groupData.groupName && !expandedSeriesGroups[expanded]) {
						canExpand = true;
					}
				}
			});
		});
		return canExpand;
	}, [expandedSeriesGroups]);

	// EXPORT WIZARD MODAL
	const exportWizardModal = useModalState<ExportOptions>();

	return (
		<>
			<Container>
				<Dropdown
					toggleComponent={({ ref, open, toggle }) => (
						<Icon
							dataTestId="series-options-toggle"
							variant={v => v.button}
							ref={ref}
							svg={Svgs.More}
							active={open}
							onClick={toggle}
						/>
					)}
					width={18}
					offset={{ top: 20, right: 0 }}
				>
					{allowToggleGroupsFeature && (
						<>
							<Dropdown.Item
								title={translation.collapseAllGroups}
								onClick={() => collapseAllSeriesGroups(seriesData.setData)}
								disabled={!canCollapseGroups}
							/>
							<Dropdown.Item
								title={translation.expandAllGroups}
								disabled={!canExpandGroups}
								onClick={() => expandAllSeriesGroups(seriesData.setData)}
							/>
						</>
					)}
					<Dropdown.Item
						title={translation.exportSeries}
						disabled={!projectId || isExportDenied}
						tooltip={exportDisabledTooltip}
						tooltipId="series-options-export-csv-tooltip"
						onClick={() =>
							exportWizardModal.open({
								type: ExportType.Series,
								format: DownloadFormat.CSV,
								...(selectedColumnsForExport?.length
									? { showFilterSection: false }
									: {})
							})
						}
					/>
					<Dropdown.Item
						title={translation.exportSeries}
						disabled={!projectId || isExportDenied}
						tooltip={exportDisabledTooltip}
						tooltipId="series-options-export-excel-tooltip"
						onClick={() =>
							exportWizardModal.open({
								type: ExportType.Series,
								format: DownloadFormat.Excel,
								...(selectedColumnsForExport?.length
									? { showFilterSection: false }
									: {})
							})
						}
					/>
				</Dropdown>
			</Container>
		</>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		collapseAllGroups: 'Collapse all groups',
		expandAllGroups: 'Expand all groups',
		exportSeries: 'Export series',
		exportDisabledTooltip: 'You do not have permission to export data from this project'
	},
	[LanguageType.Norwegian]: {
		collapseAllGroups: 'Skjul alle grupper',
		expandAllGroups: 'Ekspander alle grupper',
		exportSeries: 'Eksporter serier',
		exportDisabledTooltip: 'Du har ikke tillatelse til å eksportere data fra dette prosjektet'
	}
} as const;
